// Developer debug overlays ported from the C++ client's DEV_BUILD Test Options:
//   - DRAW_LINES (game/ht.h:92, gfDrawLines): a line in each unit/tower's side
//     colour from the unit to its current target (MobileUnitGob::DrawTargetLine
//     game/MobileUnit.cpp:468, TowerGob::DrawTargetLine game/Tower.cpp:79; render
//     loop Simulation.cpp:882).
//   - DRAW_PATHS (game/ht.h:60, gfDrawPaths): the planned navigation route of each
//     mobile unit (Path::Draw game/terrainmap.cpp:1067, MobileUnitGob::DrawPath
//     game/MobileUnit.cpp:460; render loop Simulation.cpp:810).
//
// The C++ Test Options form was intentionally not ported (ui/simuiform.ts:535,
// ui/gameoptions.ts:13). These overlays are reintroduced here, toggled live by
// Alt+L / Alt+P (web/main.ts), off by default, available in all builds.
//
// DEVIATION FROM THE ORIGINAL: Path::Draw blitted nine arrow sprites
// (arrow0..7.png + x.png). Those exist only as 832-pack build inputs
// (data/makefile.832) and are NOT in the atlas packs the web client loads, so the
// path overlay draws a polyline through the path's tile centres with a small drawn
// 'X' at the destination. Both overlays go through pbm.DrawLine, which is
// implemented on the CPU surface (gfx/dibbitmap.ts) and overridden on the WebGL2
// surface (gfx/gl/glsurface.ts), so they render in production and the ?cpu path.

import type { CpuDibBitmap } from '../gfx/dibbitmap.ts';
import type { SimUIForm } from '../ui/fingerhandler.ts';
import { kfGobMobileUnit } from '../../../sim/src/gob/gob.ts';
import type { MobileUnitGob } from '../../../sim/src/gob/mobileunit.ts';
import { kwxInvalid } from '../../../sim/src/gob/mobileunit.ts';
import type { TowerGob } from '../../../sim/src/gob/tower.ts';
import type { Path } from '../../../sim/src/terrain/terrainmap.ts';
import { kgtMachineGunTower, kgtRocketTower } from '../../../sim/src/gob/gobtypes.ts';
import { g_mpDirToDx, g_mpDirToDy } from '../../../sim/src/math/dir.ts';
import { WcFromTc } from '../../../sim/src/math/coords.ts';
import { kwcTileHalf } from '../../../sim/src/gob/unitconsts.ts';
import { GetSideColor } from '../gfx/palette.ts';

/** Live toggle state (the C++ gfDrawPaths / gfDrawLines / gfShowFPS globals).
 *  `dirty` forces one full-frame invalidate after any toggle so the overlay
 *  appears/clears at once (web/main.ts + dom/ingame/test-options-view.ts flip
 *  these; ui/simuiform.ts reads them).
 *
 *  `showFps` is the live override for the construction-time deps.fShowFPS flag
 *  (gamesession→gamerenderer→SimUIForm): the Test Options form toggles it so the
 *  FPS readout (the DOM SimUIView label / the canvas kidcFps control) appears
 *  live. SimUIForm reads `dbgOverlay.showFps || (deps.fShowFPS ?? false)`. */
export const dbgOverlay = { paths: false, lines: false, showFps: false, dirty: false };

export function toggleDebugPaths(): void {
  dbgOverlay.paths = !dbgOverlay.paths;
  dbgOverlay.dirty = true;
}

export function toggleDebugLines(): void {
  dbgOverlay.lines = !dbgOverlay.lines;
  dbgOverlay.dirty = true;
}

/** Toggle the live FPS readout (the C++ kidcShowFPS checkbox → gfShowFPS).
 *  SimUIForm reads `dbgOverlay.showFps || deps.fShowFPS` in all three FPS spots,
 *  so the DOM SimUIView label / the canvas control updates at once. */
export function toggleDebugShowFps(): void {
  dbgOverlay.showFps = !dbgOverlay.showFps;
  dbgOverlay.dirty = true;
}

/** The pixel view origin gobs are drawn against (render/simdraw.ts:450) — match it
 *  exactly so overlay lines register with the unit sprites. */
function viewOrigin(sui: SimUIForm): { xView: number; yView: number } {
  const c = sui.gctx.coords;
  const wpt = { wx: 0, wy: 0 };
  sui.GetViewPos(wpt);
  return {
    xView: c.PcFromUwc(wpt.wx),
    yView: c.PcFromUwc(wpt.wy),
  };
}

// game/Simulation.cpp:882 + MobileUnitGob/TowerGob::DrawTargetLine
export function drawDebugTargetLines(sui: SimUIForm, pbm: CpuDibBitmap): void {
  const c = sui.gctx.coords;
  const { xView, yView } = viewOrigin(sui);

  for (let pgob = sui.ctx.gobm.GetFirstGob(); pgob !== null; pgob = sui.ctx.gobm.GetNextGob(pgob)) {
    const ff = pgob.GetFlags();
    const gt = pgob.GetType();
    const fMobile = (ff & kfGobMobileUnit) !== 0;
    const fTower = gt === kgtRocketTower || gt === kgtMachineGunTower;
    if (!fMobile && !fTower) continue;

    // Both MobileUnitGob and TowerGob expose m_wptTarget; the validity sentinel is
    // m_wptTarget.wx === kwxInvalid (game/MobileUnit.cpp:470, game/Tower.cpp:81).
    const wptTarget = (pgob as MobileUnitGob | TowerGob).m_wptTarget;
    if (wptTarget.wx === kwxInvalid) continue;

    pbm.DrawLine(
      c.PcFromUwc(pgob.m_wx) - xView,
      c.PcFromUwc(pgob.m_wy) - yView,
      c.PcFromUwc(wptTarget.wx) - xView,
      c.PcFromUwc(wptTarget.wy) - yView,
      GetSideColor(pgob.GetSide()),
    );
  }
}

// game/Simulation.cpp:810 + MobileUnitGob::DrawPath + Path::Draw
export function drawDebugPaths(sui: SimUIForm, pbm: CpuDibBitmap): void {
  const c = sui.gctx.coords;
  const { xView, yView } = viewOrigin(sui);
  // quarter-tile arm for the destination 'X' (x.png is unavailable on the web)
  const xArm = Math.max(2, c.gcxTile >> 2);

  for (let pgob = sui.ctx.gobm.GetFirstGob(); pgob !== null; pgob = sui.ctx.gobm.GetNextGob(pgob)) {
    if (!(pgob.GetFlags() & kfGobMobileUnit)) continue;
    const path = (pgob as MobileUnitGob).m_ppathUnit;
    if (path === null) continue;

    drawPath(pbm, c, path, pgob.GetSide(), xView, yView, xArm);
  }
}

/** Port of Path::Draw (game/terrainmap.cpp:1067) as a polyline through tile
 *  centres, walking m_txStart/m_tyStart + the m_adir 8-direction steps. */
function drawPath(
  pbm: CpuDibBitmap,
  c: SimUIForm['gctx']['coords'],
  path: Path,
  side: number,
  xView: number,
  yView: number,
  xArm: number,
): void {
  const clr = GetSideColor(side);
  let tx = path.m_txStart;
  let ty = path.m_tyStart;
  let xPrev = c.PcFromUwc(WcFromTc(tx) + kwcTileHalf) - xView;
  let yPrev = c.PcFromUwc(WcFromTc(ty) + kwcTileHalf) - yView;

  for (let idir = 0; idir < path.m_cdirs; idir++) {
    const dir = path.m_adir[idir];
    tx += g_mpDirToDx[dir];
    ty += g_mpDirToDy[dir];
    const x = c.PcFromUwc(WcFromTc(tx) + kwcTileHalf) - xView;
    const y = c.PcFromUwc(WcFromTc(ty) + kwcTileHalf) - yView;
    pbm.DrawLine(xPrev, yPrev, x, y, clr);
    xPrev = x;
    yPrev = y;
  }

  // 'X' at the destination tile (Path::Draw's arrow index 8)
  pbm.DrawLine(xPrev - xArm, yPrev - xArm, xPrev + xArm, yPrev + xArm, clr);
  pbm.DrawLine(xPrev - xArm, yPrev + xArm, xPrev + xArm, yPrev - xArm, clr);
}
